import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator,
} from '@/components/ui/command';
import { FileText, Briefcase, Home, User, FolderOpen, Search, Clock, X } from 'lucide-react';
import { useGlobalSearch, groupSearchResults } from '@/hooks/queries/useSearch';

const RECENT_SEARCHES_KEY = 'proptrust-recent-searches';
const MAX_RECENT = 5;

function getRecentSearches(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) || '[]');
  } catch { return []; }
}

function saveRecentSearch(term: string) {
  const recent = getRecentSearches().filter(s => s !== term);
  recent.unshift(term);
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
}

function removeRecentSearch(term: string) {
  const recent = getRecentSearches().filter(s => s !== term);
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent));
}

const typeConfig: Record<string, { label: string; icon: typeof Briefcase }> = {
  deal: { label: 'Deals', icon: Briefcase },
  matter: { label: 'Matters', icon: FolderOpen },
  property: { label: 'Properties', icon: Home },
  document: { label: 'Documents', icon: FileText },
  person: { label: 'People', icon: User },
};

interface SearchCommandProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SearchCommand({ open, onOpenChange }: SearchCommandProps) {
  const [query, setQuery] = useState('');
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const navigate = useNavigate();
  const { data: results, isLoading } = useGlobalSearch(query);
  const grouped = groupSearchResults(results);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open) setQuery('');
    else setRecentSearches(getRecentSearches());
  }, [open]);

  const handleSelect = useCallback((link: string, title?: string) => {
    if (query.trim().length >= 2) saveRecentSearch(query.trim());
    if (link) { navigate(link); onOpenChange(false); }
  }, [query, navigate, onOpenChange]);

  const handleRecentClick = (term: string) => {
    setQuery(term);
  };

  const handleRemoveRecent = (e: React.MouseEvent, term: string) => {
    e.stopPropagation();
    removeRecentSearch(term);
    setRecentSearches(getRecentSearches());
  };

  const hasResults = Object.keys(grouped).length > 0;
  const showRecent = query.trim().length < 2 && recentSearches.length > 0;

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Search deals, matters, properties, documents..."
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        {showRecent && (
          <CommandGroup heading="Recent Searches">
            {recentSearches.map((term) => (
              <CommandItem key={term} value={term} onSelect={() => handleRecentClick(term)} className="cursor-pointer group">
                <Clock className="h-4 w-4 mr-2 shrink-0 text-muted-foreground" />
                <span className="flex-1 truncate">{term}</span>
                <button
                  onClick={(e) => handleRemoveRecent(e, term)}
                  className="opacity-0 group-hover:opacity-100 h-5 w-5 rounded-sm flex items-center justify-center hover:bg-muted transition-opacity"
                >
                  <X className="h-3 w-3 text-muted-foreground" />
                </button>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {query.trim().length < 2 && !showRecent ? (
          <div className="py-14 text-center">
            <Search className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-sm text-muted-foreground">Start typing to search across PropTRUST…</p>
            <p className="text-xs text-muted-foreground mt-1">Minimum 2 characters</p>
          </div>
        ) : query.trim().length < 2 ? null : isLoading ? (
          <div className="py-14 text-center">
            <div className="h-5 w-5 mx-auto border-2 border-primary border-t-transparent rounded-full animate-spin mb-3" />
            <p className="text-sm text-muted-foreground">Searching…</p>
          </div>
        ) : !hasResults ? (
          <CommandEmpty>No results found for "{query}"</CommandEmpty>
        ) : (
          Object.entries(grouped).map(([type, items]) => {
            const config = typeConfig[type] || typeConfig.document;
            const Icon = config.icon;
            return (
              <CommandGroup key={type} heading={config.label}>
                {items.map((item) => (
                  <CommandItem
                    key={item.id}
                    value={`${item.title} ${item.subtitle}`}
                    onSelect={() => handleSelect(item.link, item.title)}
                    className="cursor-pointer"
                  >
                    <Icon className="h-4 w-4 mr-2 shrink-0 text-muted-foreground" />
                    <div className="flex flex-col min-w-0">
                      <span className="truncate font-medium">{item.title}</span>
                      <span className="text-xs text-muted-foreground truncate">{item.subtitle}</span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            );
          })
        )}
      </CommandList>
    </CommandDialog>
  );
}
